<template>
  <div class="dealer-locator">
    <AppContainer size="lg" class="dealer-locator__heading">
      <AppHeading element="h1" size="xl" theme="black">
        Find A Local Dealer
      </AppHeading>
    </AppContainer>

    <AppContainer size="lg">
      <div class="dealer-locator__row dealer-locator__form">
        <div class="dealer-locator__location">
          <label class="dealer-locator__label">
            Location:
          </label>
          <form action @submit.prevent="searchDealers(location)">
            <TextInput v-model="location" placeholder="Enter zip code or address">
              <template v-slot:append>
                <button type="submit">
                  <SearchIcon />
                </button>
              </template>
            </TextInput>
          </form>
        </div>
        <div class="dealer-locator__distance">
          <label class="dealer-locator__label">
            Distance:
          </label>
          <SelectInput
            v-model="distance"
            class="dealer-locator__distance_input"
            :options="distanceOptions"
            @change="searchDealers"
          />
        </div>
        <div class="dealer-locator__products">
          <CheckboxDropdownInput v-model="selectedProducts" :options="productOptions" placeholder="Filter by Product">
            Products:
          </CheckboxDropdownInput>
        </div>
      </div>

      <div class="dealer-locator__row dealer-locator__map-container">
        <div ref="dealers" class="dealer-locator__dealers">
          <div v-if="filteredDealers.length === 0" class="dealer-locator__dealer">
            <div class="dealer-locator__dealer-content">
              <AppHeading element="h3" size="sm" theme="black">
                No Results
              </AppHeading>
            </div>
          </div>
          <div
            v-for="dealer in filteredDealers"
            :key="dealer.id"
            :ref="dealer.id"
            class="dealer-locator__dealer"
            :class="{ 'dealer-locator__dealer--active': dealer.active }"
            @click="setDealerActive(dealer)"
          >
            <div class="dealer-locator__dealer-marker">
              <AppHeading size="md" theme="black">
                {{ dealer.label }}
              </AppHeading>
            </div>

            <div class="dealer-locator__dealer-content">
              <div class="dealer-locator__dealer-distance">{{ dealer.distance }} mi</div>
              <AppHeading class="dealer-locator__dealer-heading" element="h3" size="xs" spacing="sm" theme="black">
                {{ dealer.name }}
              </AppHeading>
              <AppParagraph size="sm">
                {{ dealer.address }}
              </AppParagraph>
              <AppParagraph size="sm"> {{ dealer.city }}, {{ dealer.state }} {{ dealer.zip }} </AppParagraph>
              <AppParagraph size="sm" spacing="sm">
                <a class="dealer-locator__dealer-directions" :href="parseDirectionsLink(dealer)" target="_blank">
                  Get Directions
                </a>
              </AppParagraph>
              <AppParagraph size="sm" spacing="sm">
                {{ dealer.phone }}
              </AppParagraph>
              <div class="dealer-locator__dealer-toggle">
                Products Offerings
                <ChevronDown v-if="!dealer.active" class="dealer-locator__dealer-chevron" />
                <ChevronUp v-if="dealer.active" class="dealer-locator__dealer-chevron" />
              </div>
              <div v-show="dealer.active">
                <AppParagraph v-for="product in dealer.products" :key="product" size="sm">
                  <TextContent :content="product" class="prod-offerings" />
                </AppParagraph>
              </div>
            </div>
          </div>
        </div>
        <div v-if="loading" class="dealer-locator__map dealer-locator__map--loading">
          <img alt="Loading" :src="loadingGif" />
        </div>
        <div v-show="!loading" ref="map" class="dealer-locator__map" />
      </div>
    </AppContainer>
  </div>
</template>

<script>
import haversine from 'haversine';
import scrollIntoView from 'scroll-into-view-if-needed';
import GoogleMapsApiLoader from 'google-maps-api-loader';

import AppContainer from '@/components/shared/AppContainer';
import AppHeading from '@/components/shared/AppHeading';
import AppParagraph from '@/components/shared/AppParagraph';
import CheckboxDropdownInput from '@/components/shared/CheckboxDropdownInput';
import TextContent from '@/components/shared/TextContent';
import TextInput from '@/components/shared/TextInput';
import SelectInput from '@/components/shared/SelectInput';

import ChevronDown from '@/components/icons/ChevronDown';
import ChevronUp from '@/components/icons/ChevronUp';
import SearchIcon from '@/components/icons/SearchIcon';

const COLORS = {
  BLACK: '#000',
  PRIMARY: '#c0d52d'
};

export default {
  components: {
    AppContainer,
    AppHeading,
    AppParagraph,
    ChevronDown,
    ChevronUp,
    CheckboxDropdownInput,
    TextContent,
    TextInput,
    SearchIcon,
    SelectInput
  },
  props: {
    dealers: {
      type: [Object, Array],
      required: true
    },
    products: {
      type: [Object, Array],
      required: true
    }
  },
  data() {
    const distanceOptions = [
      { name: '50 Mi', value: '50' },
      { name: '25 Mi', value: '25' },
      { name: '10 Mi', value: '10' }
    ];

    return {
      loadingGif: window.host_domain + '/app/plugins/vue-module/dist/images/loading-leaf.gif',
      allDealers: [],
      distance: distanceOptions[0].value,
      distanceOptions,
      filteredDealers: [],
      geocoder: null,
      google: null,
      iconDefaults: {},
      labelDefaults: {
        color: COLORS.BLACK,
        fontFamily: "'Trade Gothic LT Std', system-ui, sans-serif",
        fontSize: '1.2rem',
        fontWeight: 'bold'
      },
      loading: true,
      location: null,
      map: null,
      mapDefaults: {
        center: {
          lat: 39.8283,
          lng: -98.5795
        },
        zoom: 4
      },
      selectedProducts: this.$route.query.product ? [this.$route.query.product] : []
    };
  },
  computed: {
    availableProducts() {
      return this.products.filter(p => this.dealers.some(d => d.products.includes(p.slug)));
    },
    productOptions() {
      return this.availableProducts.map(p => ({
        name: p.name,
        value: p.slug
      }));
    }
  },
  watch: {
    selectedProducts() {
      this.searchDealers();
    }
  },
  async beforeMount() {
    this.google = await GoogleMapsApiLoader({
      apiKey: process.env.VUE_APP_GOOGLE_API_KEY
    });

    this.geocoder = new this.google.maps.Geocoder();
    this.mapInit(this.$route.query);
    this.allDealers = this.dealers.map(dealer => this.addDealerMetaData(dealer));
    this.setIconDefaults(this.google);
    if (this.$route.query.zip_code) this.setZipCode(this.$route.query.zip_code);
  },
  methods: {
    addDealerMetaData(dealer) {
      return {
        ...dealer,
        active: false,
        label: '',
        marker: null,
        infoWindow: null
      };
    },
    clearMarkers() {
      this.filteredDealers.forEach((dealer, index) => {
        dealer.marker.setMap(null);
      });
    },
    drawMarkers() {
      const bounds = new this.google.maps.LatLngBounds();
      this.filteredDealers.forEach((dealer, index) => {
        const latLng = {
          lat: dealer.location.latitude,
          lng: dealer.location.longitude
        };
        const label = this.numberToLetter(index);
        bounds.extend(latLng);
        dealer.label = label;
        dealer.marker = new this.google.maps.Marker({
          position: latLng,
          label: { ...this.labelDefaults, text: label },
          icon: { ...this.iconDefaults, url: '/map-green-pin.svg' },
          optimized: false,
          map: this.map
        });
        dealer.infoWindow = new this.google.maps.InfoWindow({
          content: this.parseInfoWindowContent(dealer)
        });
        dealer.marker.addListener('click', () => {
          this.setDealerActive(dealer);
        });
      });
      if (this.filteredDealers.length === 1) {
        this.map.setZoom(10);
      } else {
        this.map.fitBounds(bounds);
      }
    },
    formatMapData(lat, lng, zoom = 10) {
      return {
        center: { lat, lng },
        zoom
      };
    },
    geoCodeLocation(address) {
      return new Promise((resolve, reject) => {
        this.geocoder.geocode({ address }, (response, status) => {
          if (status === 'OK') {
            const location = response[0].geometry.location;
            resolve({
              latitude: location.lat(),
              longitude: location.lng()
            });
          } else {
            reject();
          }
        });
      });
    },
    getInitMapData(queryParams) {
      return new Promise((resolve, reject) => {
        if (queryParams.address) {
          this.location = queryParams.address;
          this.geoCodeLocation(queryParams.address).then(geoData => {
            resolve(this.formatMapData(geoData.latitude, geoData.longitude));
          });
        } else if (queryParams.latitude && queryParams.longitude) {
          resolve(this.formatMapData(parseFloat(queryParams.latitude), parseFloat(queryParams.longitude)));
        } else if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            position => {
              resolve(this.formatMapData(position.coords.latitude, position.coords.longitude));
            },
            error => {
              resolve(null);
            }
          );
        } else {
          resolve(null);
        }
      });
    },
    async mapInit(queryParams) {
      const mapData = await this.getInitMapData(queryParams);
      this.loading = false;
      this.map = new this.google.maps.Map(this.$refs.map, mapData ? mapData : this.mapDefaults);
      if (mapData) {
        if (!this.location) {
          this.location = await this.reverseGeoCode(mapData.center);
        }
        this.searchDealers(mapData.center);
      }
      this.google.maps.event.addListener(this.map, 'click', () => {
        this.setAllDealersInactive();
      });
    },
    numberToLetter(n) {
      // https://github.com/matthewmueller/number-to-letter
      // Simple utility to convert an arbitrary number to a letter.
      const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const base = alphabet.length;
      let digits = [];

      do {
        let v = n % base;
        digits.push(v);
        n = Math.floor(n / base);
      } while (n-- > 0);

      let chars = [];
      while (digits.length) {
        chars.push(alphabet[digits.pop()]);
      }

      return chars.join('');
    },
    parseDirectionsLink(dealer) {
      return `http://maps.google.com/?q=${dealer.name}, ${dealer.address}, ${dealer.zip}`;
    },
    parseInfoWindowContent(dealer) {
      return `
        <div class="dealer-locator__info-window">
          <p class="dealer-locator__info-window__distance">
            ${dealer.distance} mi
          </p>
          <h3 class="dealer-locator__info-window__heading">${dealer.name}</h3>
          <p>${dealer.address}</p>
          <p>${dealer.city}, ${dealer.state} ${dealer.zip}</p>
          <p>
            <a href="${this.parseDirectionsLink(dealer)}" target="_blank">
              Get Directions
            </a>
          </p>
          <br />
          <p>${dealer.phone}</p>
          <br />
          <p class="dealer-locator__info-window__sub-heading">Product Offerings:</p>
          <p class="prod-offerings">
            ${dealer.products
              .map(product => product)
              .join(', ')
              .replace(/®/g, '<sup>®</sup>')}
          </p>
        </div>`;
    },
    reverseGeoCode(center) {
      return new Promise((resolve, reject) => {
        this.geocoder.geocode({ location: center }, (response, status) => {
          if (status === 'OK') {
            const zip_component = response[0].address_components.find(component => component.types.includes('postal_code'));
            if (zip_component) {
              resolve(zip_component.short_name);
              return;
            }
          }
          reject();
        });
      });
    },
    async searchDealers() {
      if (!this.location) {
        return;
      }

      const locationData = await this.geoCodeLocation(this.location);
      this.clearMarkers();
      this.map.setCenter({
        lat: locationData.latitude,
        lng: locationData.longitude
      });
      this.filteredDealers = this.allDealers
        .filter(dealer => {
          let distance = haversine(dealer.location, locationData, {
            unit: 'mile'
          });
          dealer.distance = Math.round(distance);
          const hasSelectedProducts = this.selectedProducts.every(product => dealer.products.includes(product));
          return distance <= parseInt(this.distance) && hasSelectedProducts;
        })
        .sort((a, b) => {
          return a.distance > b.distance ? 1 : -1;
        });
      if (this.filteredDealers.length > 0) {
        this.drawMarkers();
      }
      this.$refs.dealers.scrollTop = 0;
    },
    setAllDealersInactive() {
      this.filteredDealers.forEach(dealer => {
        dealer.active = false;
        dealer.marker.setLabel({
          ...this.labelDefaults,
          color: COLORS.BLACK,
          text: dealer.label
        });
        dealer.marker.setIcon({
          ...this.iconDefaults,
          url: '/map-green-pin.svg'
        });
        dealer.infoWindow.close();
      });
    },
    setDealerActive(dealer) {
      this.setAllDealersInactive();
      dealer.active = true;
      dealer.infoWindow.open(this.map, dealer.marker);
      dealer.marker.setLabel({
        ...this.labelDefaults,
        color: COLORS.PRIMARY,
        text: dealer.label
      });
      dealer.marker.setIcon({
        ...this.iconDefaults,
        url: '/map-black-pin.svg'
      });
      this.$nextTick(() => {
        const mobileWidth = window.matchMedia('(max-width: 60em)');
        if (!mobileWidth.matches) {
          scrollIntoView(this.$refs[dealer.id][0], {
            behavior: 'smooth',
            block: 'start',
            scrollMode: 'if-needed'
          });
        }
      });
    },
    setIconDefaults(google) {
      this.iconDefaults = {
        anchor: new google.maps.Point(0, 39),
        labelOrigin: new google.maps.Point(13, 16),
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(26, 39)
      };
    },
    getProductName(product) {
      return this.products.find(p => p.slug === product);
    },
    setZipCode(zipCode) {
      if (zipCode) {
        this.geocoder.geocode({ address: zipCode }, (results, status) => {
          if (status === 'OK') {
            this.location = zipCode;
            this.map.setZoom(10);
            this.searchDealers();
          }
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.dealer-locator {
  @include font('open-sans', 'normal');
  &__heading {
    margin-bottom: spacing('xl', 2);
    margin-top: spacing('xl', 2);
    text-align: center;
  }

  &__row {
    display: flex;
    margin-bottom: spacing('xl');
  }

  &__form {
    flex-wrap: wrap;
  }

  &__label {
    @include font-size('paragraph', 'sm');
    color: color('gray');
    display: block;
    font-weight: 700;
    margin-bottom: spacing('sm');
  }

  &__location {
    flex: 0 0 100%;
    max-width: 100%;
    padding-bottom: spacing('lg');

    @include breakpoint('md') {
      flex: 0 0 50%;
      max-width: 50%;
      padding-bottom: 0;
      padding-right: spacing('lg');
    }
  }

  &__distance {
    flex: 0 0 40%;
    font-size: 80%;
    max-width: 40%;
    padding-right: spacing('lg');

    @include breakpoint('md') {
      flex: 0 0 15%;
      max-width: 15%;
    }
  }

  &__products {
    flex: 0 0 60%;
    max-width: 60%;
    position: relative;

    @include breakpoint('md') {
      flex: 0 0 35%;
      max-width: 35%;
    }
  }

  &__map-container {
    flex-direction: column-reverse;

    @include breakpoint('md') {
      flex-direction: row;
    }
  }

  &__dealers {
    border: solid 1px shade('white', 7%);

    @include breakpoint('md') {
      flex: 0 0 30%;
      height: 40rem;
      max-width: 30%;
      overflow-y: auto;
    }
  }

  &__dealer {
    @include font-size('paragraph', 'sm');
    background-color: color('light');
    border-bottom: solid 1px shade('white', 7%);
    display: flex;
    flex-direction: row;
    padding-bottom: 5px;
    position: relative;

    &--active {
      background-color: color('white');
      border-bottom-color: color('primary');

      &::after {
        background-color: color('primary');
        bottom: 0;
        content: '';
        height: 5px;
        left: 0;
        position: absolute;
        width: 100%;
      }
    }
  }

  &__dealer-marker {
    flex-shrink: 1;
    padding: spacing('md');
    padding-right: 0;
  }

  &__dealer-content {
    flex-grow: 1;
    padding: spacing('md');
  }

  &__dealer-distance {
    float: right;
    padding-left: spacing('md');
  }

  &__dealer-directions {
    text-decoration: underline;
  }

  &__dealer-toggle {
    font-weight: bold;
  }

  .paragraph--sm span.prod-offerings {
    text-transform: capitalize;
  }

  &__dealer-chevron {
    color: color('primary');
    height: 15px;
    margin-left: spacing('xs');
    transform: translateY(3px);
    width: 15px;
  }

  &__map {
    height: 20rem;

    @include breakpoint('md') {
      flex: 0 0 70%;
      height: 40rem;
      max-width: 70%;
    }

    &--loading {
      align-items: center;
      background-color: #fbfbfb;
      display: flex;
      justify-content: center;
    }
  }

  :deep(.gm-style .gm-style-iw-c) {
    border-radius: 0;
  }

  :deep(.dealer-locator__info-window) {
    line-height: 1.5;
    padding: spacing('xs');

    a {
      text-decoration: underline;
    }

    .dealer-locator__info-window__heading {
      color: color('black');
      margin-bottom: spacing('sm');
      text-transform: uppercase;
    }

    .dealer-locator__info-window__sub-heading {
      color: color('black');
      font-weight: 700;
    }

    .dealer-locator__info-window__distance {
      float: right;
      padding-left: spacing('xs');
    }

    p.prod-offerings {
      text-transform: capitalize;
    }
  }
}
</style>